import React, { useState, useEffect } from 'react';
import { BarChart3, Trophy, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Loader2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCell = ({ value, label, color }) => (
  <div className="text-center">
    <div className={`text-sm font-bold ${color}`}>{value}</div>
    <div className="text-[8px] text-gray-400 uppercase">{label}</div>
  </div>
);

export const TargetingInsights = ({ roleId }) => {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (expanded && !data) loadInsights();
  }, [expanded]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/roles/${roleId}/targeting-insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!d.error) setData(d);
    } catch { /* silent */ }
    setLoading(false);
  };

  const versions = data?.versions || [];
  const bestVersion = data?.best_version;
  const hasData = versions.some(v => v.stats.total > 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="targeting-insights">
      <button
        onClick={() => setExpanded(s => !s)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
        data-testid="targeting-insights-toggle"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span className="text-[11px] font-bold text-[#0A1F44]">Targeting Insights</span>
          {data && (
            <span className="text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
              {data.total_versions} version{data.total_versions !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          ) : !hasData ? (
            <p className="text-[10px] text-gray-400 italic py-2">No sourcing data yet. Source candidates to see insights.</p>
          ) : (
            <>
              {/* Version Comparison */}
              <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(versions.length, 4)}, 1fr)` }} data-testid="targeting-versions-grid">
                {versions.map((v, i) => {
                  const isBest = v.version === bestVersion && versions.length > 1;
                  const imp = v.improvement;
                  const s = v.stats;
                  return (
                    <div key={v.version}
                      className={`p-2.5 rounded-lg border transition-all ${isBest ? 'border-emerald-300 bg-emerald-50/50 ring-1 ring-emerald-200' : 'border-gray-200 bg-white'}`}
                      data-testid={`targeting-version-${v.version}`}>
                      {/* Version header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold text-[#0A1F44]">{v.version.toUpperCase()}</span>
                          {isBest && (
                            <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded-full" data-testid={`best-badge-${v.version}`}>
                              <Trophy className="h-2 w-2" /> BEST
                            </span>
                          )}
                        </div>
                        {imp && (
                          <span className={`flex items-center gap-0.5 text-[8px] font-bold ${imp.score_delta > 0 ? 'text-emerald-600' : imp.score_delta < 0 ? 'text-red-500' : 'text-gray-400'}`}
                            data-testid={`improvement-${v.version}`}>
                            {imp.score_delta > 0 ? <TrendingUp className="h-2.5 w-2.5" /> : imp.score_delta < 0 ? <TrendingDown className="h-2.5 w-2.5" /> : null}
                            {imp.score_delta > 0 ? '+' : ''}{imp.score_delta}
                          </span>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-1 mb-2">
                        <StatCell value={s.avg_score} label="Avg Score"
                          color={s.avg_score >= 50 ? 'text-emerald-600' : s.avg_score >= 30 ? 'text-amber-600' : 'text-gray-500'} />
                        <StatCell value={s.high_fit} label="Strong Fit"
                          color={s.high_fit > 0 ? 'text-emerald-600' : 'text-gray-400'} />
                        <StatCell value={s.total} label="Total" color="text-[#0A1F44]" />
                      </div>

                      {/* Score bar */}
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${s.avg_score >= 50 ? 'bg-emerald-500' : s.avg_score >= 30 ? 'bg-amber-400' : 'bg-gray-300'}`}
                          style={{ width: `${s.avg_score}%` }} />
                      </div>

                      {/* Fit distribution mini bar */}
                      {s.total > 0 && (
                        <div className="flex h-1 mt-1 rounded-full overflow-hidden bg-gray-100">
                          {s.high_fit > 0 && <div className="bg-emerald-500" style={{ width: `${(s.high_fit / s.total) * 100}%` }} />}
                          {s.medium_fit > 0 && <div className="bg-amber-400" style={{ width: `${(s.medium_fit / s.total) * 100}%` }} />}
                          {s.low_fit > 0 && <div className="bg-gray-300" style={{ width: `${(s.low_fit / s.total) * 100}%` }} />}
                        </div>
                      )}

                      {/* Criteria summary */}
                      <div className="mt-2 space-y-0.5">
                        {v.label && <p className="text-[8px] text-gray-400">{v.label}</p>}
                        {v.criteria?.search_titles && (
                          <p className="text-[8px] text-gray-500 truncate" title={v.criteria.search_titles.join(', ')}>
                            Titles: {v.criteria.search_titles.slice(0, 3).join(', ')}{v.criteria.search_titles.length > 3 ? '...' : ''}
                          </p>
                        )}
                        {v.criteria?.seniority && <p className="text-[8px] text-gray-500">Seniority: {v.criteria.seniority}</p>}
                      </div>

                      {/* Improvement details */}
                      {imp && (
                        <div className="mt-1.5 pt-1.5 border-t border-gray-100 space-y-0.5">
                          {imp.score_delta !== 0 && (
                            <p className={`text-[8px] font-medium ${imp.score_delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              Score {imp.score_delta > 0 ? 'improvement' : 'decrease'}: {imp.score_delta > 0 ? '+' : ''}{imp.score_delta} avg
                            </p>
                          )}
                          {imp.high_fit_delta !== 0 && (
                            <p className={`text-[8px] font-medium ${imp.high_fit_delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              Strong-fit {imp.high_fit_delta > 0 ? 'increase' : 'decrease'}: {imp.high_fit_delta > 0 ? '+' : ''}{imp.high_fit_delta}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary insight */}
              {versions.length > 1 && bestVersion && (
                <div className="p-2 bg-[#0A1F44]/[0.03] border border-[#0A1F44]/10 rounded-lg" data-testid="targeting-summary">
                  <p className="text-[9px] text-[#0A1F44]">
                    <strong>{bestVersion.toUpperCase()}</strong> is the best-performing version with
                    <strong> {versions.find(v => v.version === bestVersion)?.stats.avg_score}</strong> avg score
                    {versions.find(v => v.version === bestVersion)?.stats.high_fit > 0 && (
                      <> and <strong>{versions.find(v => v.version === bestVersion)?.stats.high_fit}</strong> strong-fit candidates</>
                    )}.
                    {versions.length >= 2 && versions[versions.length - 1].improvement?.score_delta > 0 && (
                      <> Latest targeting improved avg score by <strong>+{versions[versions.length - 1].improvement.score_delta}</strong>.</>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
